import React from 'react';
import Navbar from '../commonComponents/Navbar';
import Footer from '../commonComponents/Footer';
import { Box, Container, Grid, Typography } from '@mui/material';
import useScrollAnimation from '../../hooks/useScrollAnimation';
import ServicesHero from '../commonComponents/ServicesHero';
import aboutHeroImg from "../../assets/images/about_banner_3.png";
import teamImg from "../../assets/images/team.png";
import SmallCard from '../commonComponents/SmallCard';

import lockIcon from '../../assets/images/lock_icon.png'
import shieldIcon from '../../assets/images/shield_icon.png'
import layerIcon from '../../assets/images/layer_icon.png'
import exchangeIcon from '../../assets/images/Exchange_icon.png'
import bookImg from '../../assets/images/book_image.png'

import ServiceCard from '../commonComponents/ServiceBusinessCard'


const About = () => {
  const [storyRef, storyVisible] = useScrollAnimation(0.15);
  const [corporateRef, corporateVisible] = useScrollAnimation(0.08);
  const [whyHeaderRef, whyHeaderVisible] = useScrollAnimation(0.2);
  const [whyCardsRef, whyCardsVisible] = useScrollAnimation(0.1);

  const storyEase = 'cubic-bezier(0.16, 1, 0.3, 1)';
  const storyDur = '1.05s';
  const fadeUpStory = (delay) => ({
    opacity: storyVisible ? 1 : 0,
    transform: storyVisible ? 'translateY(0)' : 'translateY(28px)',
    transition: `opacity ${storyDur} ${storyEase} ${delay}s, transform ${storyDur} ${storyEase} ${delay}s`,
  });

  const ease = 'cubic-bezier(0.16, 1, 0.3, 1)';
  const dur = '1.05s';
  const fadeRight = (delay) => ({
    opacity: corporateVisible ? 1 : 0,
    transform: corporateVisible ? 'translateX(0)' : 'translateX(45px)',
    transition: `opacity ${dur} ${ease} ${delay}s, transform ${dur} ${ease} ${delay}s`,
  });
  const fadeLeft = (delay) => ({
    opacity: corporateVisible ? 1 : 0,
    transform: corporateVisible ? 'translateX(0)' : 'translateX(-40px)',
    transition: `opacity ${dur} ${ease} ${delay}s, transform ${dur} ${ease} ${delay}s`,
  });

  const cards = [
    {
      icon: <img src={lockIcon} alt="Crypto Access"
        style={{
          width: 64,
          height: 64,
          objectFit: "contain",
        }}
      />,
      title: "Simplifying Crypto Access",
      description:
        "Digital assets were often complex and difficult to navigate. We created Payible to make digital assets simpler, secure, and easier to use.",
    },
    {
      icon: <img src={shieldIcon} alt="Crypto Access"
        style={{
          width: 64,
          height: 64,
          objectFit: "contain",
        }}
      />,
      title: "Building Trust & Reliability",
      description:
        "The industry needed a more dependable and accessible payment experience. Payible delivers enterprise-grade security with consumer-friendly simplicity.",
    },
    {
      icon: <img src={layerIcon} alt="Crypto Access"
        style={{
          width: 64,
          height: 64,
          objectFit: "contain",
        }}
      />,
      title: "Driving Wider Adoption",
      description:
        "By removing complexity and building intuitive experiences, we're helping bring digital assets to mainstream users worldwide.",
    },
  ];


  return (
    <div style={{ backgroundColor: 'var(--color-bg)', color: '#fff', minHeight: '100vh' }} className="page-enter">
      <Navbar />

      <ServicesHero
        heading="About Us"
        highlightedText=""
        description="Built on decades of experience across cryptocurrency, payments, and financial technology. Our journey through the evolution of digital assets continues to shape the infrastructure we build today."
        subDescription=""
        image={aboutHeroImg}
      />

      {/* Hero Section */}
      <Container>

        <Box
          ref={storyRef}
          className={`anim-fade-up${storyVisible ? ' in-view' : ''}`}
          sx={{ my: { xs: 10, md: 12 } }}
        >
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              fontSize: { xs: "1.8rem", md: "19px" },
              mb: 3.5,
              fontFamily: "var(--font-primary)",
            }}
          >
            The Payible Story
          </Typography>
          <Typography
            sx={{
              color: "rgba(255, 255, 255, 0.65)",
              fontSize: "1.05rem",
              lineHeight: 1.85,
              mb: 3.5,
              fontFamily: "var(--font-secondary)",
            }}
          >
            Founded in 2023, Payible leverages over 20 years of collective expertise across cryptocurrency, payments, and fintech to deliver innovative solutions. Our team has been active in the digital asset space since 2015, spanning across OTC, Exchanges, Fintech, AML and compliance operations, and supporting both investors and everyday crypto users.
          </Typography>
          <Typography
            sx={{
              color: "rgba(255, 255, 255, 0.65)",
              fontSize: "1.05rem",
              lineHeight: 1.85,
              fontFamily: "var(--font-secondary)",
            }}
          >
            We've witnessed the evolution of the industry firsthand, from early adoption through to global expansion, giving us a deep understanding of both the opportunities and the risks within this space. This experience shapes Payible today - building infrastructure that is simple to use, reliable and secure to operate, and aligned with modern regulatory expectations.
          </Typography>
        </Box>
      </Container>

      {/* Corporate Overview */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
        <Grid
          ref={corporateRef}
          container
          spacing={{ xs: 4, md: 8 }}
          sx={{ alignItems: 'center' }}
        >
          {/* Left Side Image — animates first */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Box
              component="img"
              src={teamImg}
              alt="Our Team"
              sx={{
                width: "100%",
                height: {
                  xs: 300,
                  sm: 450,
                  md: 550,
                  lg: 750,
                },
                objectFit: "cover",
                borderRadius: "24px",
                display: "block",
                boxShadow: "var(--fs-card-background-shadow)",
                ...fadeLeft(0),
              }}
            />
          </Grid>

          {/* Right Side Content — each element staggers after image */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ px: { xs: 0, md: 2 } }}>
              <Typography
                sx={{
                  color: "var(--color-primary)",
                  fontWeight: 700,
                  fontFamily: "var(--font-primary)",
                 fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2.5rem" },
                  ...fadeRight(0.35),
                }}
              >
                Our Mission
              </Typography>

              <Typography
                sx={{
                  color: "rgba(255,255,255,0.75)",
                  fontFamily: "var(--font-secondary)",
                  fontSize: { xs: "15px", sm: "16px", md: "15px" },
                  lineHeight: '35px',
                  letterSpacing: '2%',
                  mb: 5,
                  ...fadeRight(0.5),
                }}
              >
                To make digital assets simple and accessible for everyday use. We
                believe accessing and accepting cryptocurrency should be as easy as
                paying a bill or sending an email—seamless, reliable, and intuitive.
              </Typography>

              <Typography
                sx={{
                  color: "var(--color-primary)",
                  fontWeight: 700,
                  fontFamily: "var(--font-primary)",
                  fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2.5rem" },
                  ...fadeRight(0.65),
                }}
              >
                Our Standards
              </Typography>

              <Typography
                sx={{
                  color: "rgba(255,255,255,0.75)",
                  fontFamily: "var(--font-secondary)",
                  fontSize: { xs: "15px", sm: "16px", md: "15px" },
                  lineHeight: '35px',
                  letterSpacing: '2%',
                  ...fadeRight(0.8),
                }}
              >
                Our team operates across multiple regions, providing responsive support to customers around the world. As Payible continues to grow, we remain focused on expanding our presence and supporting more jurisdictions globally.

                <br />
                <br />
                We take a compliance-first approach to every market we enter, prioritising regulatory alignment and strong governance frameworks. This commitment provides our customers and partners with confidence as they scale their digital asset operations.

                <br />
                <br />
                Our growth is driven by execution. By continually expanding our payment rails and infrastructure capabilities, we are building faster, more reliable pathways for digital asset transactions across the globe.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>

      <Box sx={{ pt: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          {/* Header Section */}
          <Box
            ref={whyHeaderRef}
            className={`anim-fade-up${whyHeaderVisible ? ' in-view' : ''}`}
            textAlign="center"
            mb={8}
          >
            <Typography
              variant="h2"
              component="h1"
              sx={{
                fontWeight: 700,
                color: '#fff',
                mb: 5,
                fontSize: { xs: '2rem', md: '2.5rem' },
                letterSpacing: '-0.02em',
              }}
            >
              Why We Built Payible
            </Typography>
          </Box>

          {/* Cards Grid */}
          <Grid
            container
            spacing={4}
            ref={whyCardsRef}
            sx={{
              justifyContent: "center",
              alignItems: "stretch",
            }}
          >
            {cards.map((card, index) => (
              <Grid
                key={index}
                size={{ xs: 12, md: 4 }}
                className={`anim-fade-up anim-d${index}${whyCardsVisible ? ' in-view' : ''}`}
                sx={{
                  display: "flex",
                  boxShadow: "var(--fs-card-background-shadow)",
                }}
              >
                <SmallCard
                  icon={card.icon}
                  title={card.title}
                  description={card.description}
                />
              </Grid>
            ))}
          </Grid>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: { xs: 10, md: 16 },
              my: { xs: 10, md: 16 },
            }}
          >

            <ServiceCard
              image={bookImg}
              badgeText=""
              badgeIcon={

                <Box
                  component="img"
                  src={exchangeIcon}
                  alt="icon"
                  sx={{
                    width: "3rem",
                    height: "3rem",
                    objectFit: "contain",
                  }}
                />

              }
              title="Looking Ahead"
              description1={
                <>
                  Our roadmap includes expanding global payment rails, enhancing real-time settlement infrastructure, and deepening our regulatory footprint across key regions.
                  <br />
                  <br />
                  Payible is building the foundation for the next era of digital payments.
                </>
              }
              description2=""
            />
          </Box>

        </Container>
      </Box>

      <Footer />
    </div>
  );
};

export default About;
